import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Trophy, Euro, Package, Truck, MapPin, Calendar, MessageSquare,
  Check, X, ArrowDownUp, Loader2, ShieldCheck,
} from 'lucide-react';
import {
  getOffersForWanted,
  acceptStructuredOffer,
  declineStructuredOffer,
  counterStructuredOffer,
  confirmStructuredOffer,
} from '@/lib/api';
import { fmt2 } from '@/lib/utils';

const STATUS_BADGE = {
  submitted: { label: 'Eingegangen', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  viewed_by_buyer: { label: 'Gesehen', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
  accepted: { label: 'Angenommen – Verkäufer-Bestätigung offen', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  declined: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  countered: { label: 'Gegenangebot', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  withdrawn: { label: 'Zurückgezogen', cls: 'bg-slate-100 text-slate-500' },
  expired: { label: 'Abgelaufen', cls: 'bg-slate-100 text-slate-500' },
  transaction_created: { label: 'Transaktion erfasst', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
};

export function OfferComparisonDialog({ open, onClose, ad, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [bestOfferId, setBestOfferId] = useState(null);
  const [counterFor, setCounterFor] = useState(null);
  const [sortBy, setSortBy] = useState('best');
  const [actionLoading, setActionLoading] = useState(null);

  const load = async () => {
    if (!ad?.wanted_id) return;
    setLoading(true);
    try {
      const res = await getOffersForWanted(ad.wanted_id);
      setOffers(res.data.offers || []);
      setBestOfferId(res.data.best_offer_id || null);
    } catch (e) {
      toast.error('Fehler beim Laden der Angebote.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ad?.wanted_id]);

  const sortedOffers = (() => {
    const arr = [...offers];
    if (sortBy === 'price_asc') return arr.sort((a, b) => a.unit_price_eur - b.unit_price_eur);
    if (sortBy === 'price_desc') return arr.sort((a, b) => b.unit_price_eur - a.unit_price_eur);
    if (sortBy === 'newest') return arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    // best: open offers first (by status priority), then best-flag, then unit_price asc
    const statusRank = (s) => ({ submitted: 0, viewed_by_buyer: 0, accepted: 1, countered: 2, declined: 3, withdrawn: 4, expired: 5, transaction_created: 6 }[s] ?? 9);
    return arr.sort((a, b) => {
      if (a.is_best_offer && !b.is_best_offer) return -1;
      if (!a.is_best_offer && b.is_best_offer) return 1;
      const r = statusRank(a.status) - statusRank(b.status);
      if (r !== 0) return r;
      return a.unit_price_eur - b.unit_price_eur;
    });
  })();

  const handleAccept = async (offerId) => {
    setActionLoading(offerId + ':accept');
    try {
      await acceptStructuredOffer(offerId);
      toast.success('Angebot angenommen. Wartet auf Verkäufer-Bestätigung.');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (offerId) => {
    if (!window.confirm('Angebot wirklich ablehnen?')) return;
    setActionLoading(offerId + ':decline');
    try {
      await declineStructuredOffer(offerId);
      toast.success('Angebot abgelehnt.');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="offer-comparison-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <Trophy className="h-5 w-5 text-orange-500" />
              Eingegangene Angebote vergleichen
            </DialogTitle>
            <DialogDescription>
              <span className="font-medium text-foreground">{ad?.material_type_description}</span>
              <span className="text-muted-foreground"> · {ad?.min_quantity} {ad?.quantity_unit} gesucht</span>
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between gap-2 py-2">
            <span className="text-sm text-muted-foreground" data-testid="offer-count">
              {loading ? '…' : `${offers.length} Angebot(e)`}
            </span>
            <div className="flex items-center gap-2">
              <ArrowDownUp className="h-4 w-4 text-muted-foreground" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48 h-8 text-xs" data-testid="offer-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="best">Beste zuerst</SelectItem>
                  <SelectItem value="price_asc">Preis aufsteigend</SelectItem>
                  <SelectItem value="price_desc">Preis absteigend</SelectItem>
                  <SelectItem value="newest">Neueste zuerst</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-36" />)}
            </div>
          ) : sortedOffers.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground" data-testid="offer-empty">
              <Package className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">Noch keine Angebote eingegangen</p>
              <p className="text-sm mt-1">Verkäufer werden benachrichtigt, sobald passendes Material gelistet ist.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="offer-list">
              {sortedOffers.map((o) => (
                <OfferCard
                  key={o.offer_id}
                  offer={o}
                  isBest={o.is_best_offer}
                  buyerCurrency="EUR"
                  onAccept={() => handleAccept(o.offer_id)}
                  onDecline={() => handleDecline(o.offer_id)}
                  onCounter={() => setCounterFor(o)}
                  actionLoading={actionLoading}
                />
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {counterFor && (
        <CounterOfferDialog
          open={!!counterFor}
          onClose={() => setCounterFor(null)}
          offer={counterFor}
          onDone={() => { setCounterFor(null); load(); onChanged?.(); }}
        />
      )}
    </>
  );
}

function OfferCard({ offer, isBest, onAccept, onDecline, onCounter, actionLoading }) {
  const sb = STATUS_BADGE[offer.status] || { label: offer.status, cls: 'bg-slate-100' };
  const canAct = ['submitted', 'viewed_by_buyer'].includes(offer.status);
  const isParentCountered = offer.status === 'countered';
  const isAccepted = offer.status === 'accepted';
  const isDone = offer.status === 'transaction_created';

  return (
    <Card className={`relative overflow-hidden ${isBest ? 'border-orange-500 border-2 shadow-md' : ''}`} data-testid={`offer-card-${offer.offer_id}`}>
      {isBest && (
        <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg flex items-center gap-1" data-testid="best-offer-badge">
          <Trophy className="h-3 w-3" />
          Bestes Angebot
        </div>
      )}
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3 pr-24">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge className={`text-xs ${sb.cls}`} data-testid="offer-status">{sb.label}</Badge>
              {offer.is_buyer_counter && <Badge variant="outline" className="text-xs">Gegenangebot</Badge>}
            </div>
            <p className="font-semibold text-sm">
              {offer.seller_name || offer.seller_group_name || 'Anbieter'}
              {offer.seller_group_name && offer.seller_name && (
                <span className="text-muted-foreground font-normal"> · {offer.seller_group_name}</span>
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Metric icon={<Euro className="h-3.5 w-3.5 text-orange-500" />} label="Stückpreis" value={`${fmt2(offer.unit_price_eur)} €`} />
          <Metric icon={<Euro className="h-3.5 w-3.5 text-green-600" />} label="Gesamtpreis" value={<span className="font-bold">{fmt2(offer.total_price_eur)} €</span>} />
          <Metric icon={<Package className="h-3.5 w-3.5 text-blue-500" />} label="Menge" value={`${offer.offered_qty} ${offer.quantity_unit}`} />
          <Metric icon={<Truck className="h-3.5 w-3.5 text-purple-500" />} label="Incoterm" value={offer.incoterm} />
          {offer.pickup_site && (
            <Metric
              icon={<MapPin className="h-3.5 w-3.5 text-emerald-600" />}
              label="Standort"
              value={`${offer.pickup_site.city || ''} ${offer.pickup_site.country || ''}`.trim()}
            />
          )}
          {offer.available_from && (
            <Metric icon={<Calendar className="h-3.5 w-3.5 text-amber-500" />} label="Verfügbar ab" value={offer.available_from} />
          )}
          <Metric icon={<Calendar className="h-3.5 w-3.5 text-slate-500" />} label="Gültig bis" value={offer.offer_valid_until} />
        </div>

        {offer.message && (
          <div className="mt-3 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
            <p className="text-xs text-muted-foreground mb-0.5 flex items-center gap-1"><MessageSquare className="h-3 w-3" />Nachricht</p>
            <p className="whitespace-pre-wrap">{offer.message}</p>
          </div>
        )}

        {canAct && (
          <div className="mt-3 pt-3 border-t flex items-center justify-end gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={onCounter}
              disabled={!!actionLoading}
              data-testid={`offer-counter-${offer.offer_id}`}
            >
              <ArrowDownUp className="h-3.5 w-3.5 mr-1" /> Gegenangebot
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={onDecline}
              disabled={!!actionLoading}
              data-testid={`offer-decline-${offer.offer_id}`}
            >
              {actionLoading === offer.offer_id + ':decline' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <X className="h-3.5 w-3.5 mr-1" />}
              Ablehnen
            </Button>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700"
              onClick={onAccept}
              disabled={!!actionLoading}
              data-testid={`offer-accept-${offer.offer_id}`}
            >
              {actionLoading === offer.offer_id + ':accept' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              Annehmen
            </Button>
          </div>
        )}

        {isAccepted && (
          <div className="mt-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Sie haben angenommen.</p>
              <p className="text-xs mt-0.5">Der Verkäufer muss noch bestätigen — dann wird die Transaktion erfasst.</p>
            </div>
          </div>
        )}

        {isParentCountered && (
          <div className="mt-3 p-2.5 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-xs text-purple-800 dark:text-purple-200">
            Sie haben hierauf ein Gegenangebot gesendet. Wartet auf den Verkäufer.
          </div>
        )}

        {isDone && (
          <div className="mt-3 p-2.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 text-sm flex items-center gap-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-green-900 dark:text-green-200 font-medium">Deal abgeschlossen — Transaktion erfasst.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-xs text-muted-foreground">{icon}<span>{label}</span></div>
      <div className="font-medium text-sm mt-0.5 truncate">{value}</div>
    </div>
  );
}

function CounterOfferDialog({ open, onClose, offer, onDone }) {
  const [form, setForm] = useState({
    offered_qty: String(offer.offered_qty),
    unit_price_eur: String(offer.unit_price_eur),
    incoterm: offer.incoterm,
    available_from: offer.available_from || '',
    offer_valid_until: offer.offer_valid_until,
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.offered_qty || !form.unit_price_eur || !form.incoterm || !form.offer_valid_until) {
      toast.error('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }
    setLoading(true);
    try {
      await counterStructuredOffer(offer.offer_id, {
        offered_qty: parseFloat(form.offered_qty),
        unit_price_eur: parseFloat(form.unit_price_eur),
        incoterm: form.incoterm,
        available_from: form.available_from || null,
        offer_valid_until: form.offer_valid_until,
        message: form.message || null,
      });
      toast.success('Gegenangebot gesendet.');
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="counter-offer-dialog">
        <DialogHeader>
          <DialogTitle>Gegenangebot abgeben</DialogTitle>
          <DialogDescription>
            Ursprüngliches Angebot: {fmt2(offer.unit_price_eur)} €/{offer.quantity_unit} · {offer.offered_qty} {offer.quantity_unit}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Menge *</Label>
              <Input type="number" step="0.01" min="0" value={form.offered_qty} onChange={(e) => set('offered_qty', e.target.value)} data-testid="counter-qty" />
            </div>
            <div className="space-y-1.5">
              <Label>Stückpreis EUR *</Label>
              <Input type="number" step="0.01" min="0" value={form.unit_price_eur} onChange={(e) => set('unit_price_eur', e.target.value)} data-testid="counter-price" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Incoterm *</Label>
            <Select value={form.incoterm} onValueChange={(v) => set('incoterm', v)}>
              <SelectTrigger data-testid="counter-incoterm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DDP'].map((ic) => (
                  <SelectItem key={ic} value={ic}>{ic}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Gültig bis *</Label>
            <Input
              type="date"
              value={form.offer_valid_until}
              onChange={(e) => set('offer_valid_until', e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              data-testid="counter-valid-until"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Nachricht</Label>
            <Textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={2} data-testid="counter-message" />
          </div>
          <Separator />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Abbrechen</Button>
            <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={loading} data-testid="counter-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Gegenangebot senden
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
